import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'src/app/framework/service/snack-bar.service';
import { PatientService } from '../dashboard/patient/patient.service';
import { GenomeDataOrderComponent } from '../dashboard/genome-data-order/genome-data-order.component';
import { PatientConsentComponent } from '../dashboard/patient-consent/patient-consent.component';
import { EditMyConsentComponent } from './edit-my-consent/edit-my-consent.component';
import { ConsentService } from './my-consent.service';


interface Consent {
  id: number;
  identifier: string;
  description: string;
  mandatory: boolean;
}
@Component({
  selector: 'app-my-consent',
  templateUrl: './my-consent.component.html',
  styleUrls: ['./my-consent.component.scss']
})
export class MyConsentComponent implements OnInit {

  consentForm: FormGroup;
  consents: Consent[] = [];
  myFocusVar: boolean = false;
  hasOptionalConsentChanged: boolean = false;
  private optionalConsentInitialized: boolean = false;
  private initialOptionalConsentValues: { [id: number]: boolean } = {};
  patientId: number;
  id: number;
  hasKitRegistered: boolean = false;
  isLoadingKitStatus: boolean = true;


  mandatoryIdentifiers = [
    'InformedAboutDNA',
    'UnderstandsTest',
    'ConsentSampleCollection',
    'VoluntaryTest',
    'ConsentStorageUK',
    'DataAccessResults'
  ];

  analysisRequests = [
    { id: 1, description: 'I confirm that I give my consent for the selected analyses to be carried out on my whole genome data. I understand that the results will be used to generate my personal genomic report and that I may withdraw my consent  for future analyses at any time.', mandatory: false },
    { id: 2, description: 'I would like StoreGene to contact me regarding any new analyses that become available in the future.', mandatory: false },

  ];

  constructor(
    private readonly pservice: PatientService,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly snackBar: SnackBarService,
    private readonly dialog: MatDialog,
    private readonly consentService: ConsentService
  ) { }

  ngOnInit(): void {
    this.consentForm = this.fb.group({});

    this.pservice.getPatientbyId().subscribe({
      next: (patientId) => {
        this.patientId = patientId;
        this.id = patientId;
        this.checkIfTestOrdered();
        this.initializeOptionalConsent();
      },
      error: (err) => {
        console.error('Error fetching patient ID:', err);
        this.snackBar.error('Failed to load patient information');
        this.isLoadingKitStatus = false;
      }
    });

    const consentIdentifier = 'PC';
    this.pservice.getAllPatientsConsent(consentIdentifier).subscribe(data => {
      this.consents = data;
      data.forEach(c => {
        const isMandatory = this.mandatoryIdentifiers.includes(c.identifier);
        c.mandatory = isMandatory;
        const controlName = 'consent_' + c.id;
        const control = this.fb.control(false, isMandatory ? Validators.requiredTrue : []);
        
        if (this.hasKitRegistered && isMandatory) {
          control.disable();
          control.setValue(true);
        }
        
        this.consentForm.addControl(controlName, control);
      });
      
      if (this.hasKitRegistered) {
        this.disableMandatoryConsents();
      }
      this.initializeOptionalConsent();
    });
  }

  private initializeOptionalConsent(): void {
    if (this.optionalConsentInitialized) {
      return;
    }

    if (!this.patientId || !this.consentForm || !this.consents || this.consents.length === 0) {
      return;
    }

    this.optionalConsentInitialized = true;

    this.consentService.hasOptionalConsent(this.patientId).subscribe({
      next: (hasOptionalConsent: boolean) => {
        const optionalConsents = this.consents.filter(c => !c.mandatory);

        optionalConsents.forEach(c => {
          const controlName = 'consent_' + c.id;
          const control = this.consentForm.get(controlName);
          if (control) {
            control.setValue(!!hasOptionalConsent);
          }
        });

        this.captureInitialOptionalConsentValues();
      },
      error: (err) => {
        console.error('Error fetching optional consent status:', err);
        this.captureInitialOptionalConsentValues();
      }
    });
  }

  private checkIfTestOrdered(): void {
    if (this.patientId) {
      this.isLoadingKitStatus = true;
      this.pservice.getGenomeData(this.patientId).subscribe({
        next: (data) => {

          this.hasKitRegistered = data && data.id != null;
          this.isLoadingKitStatus = false;
          if (this.hasKitRegistered) {
            this.disableMandatoryConsents();
          }
        },
        error: (err) => {
          this.hasKitRegistered = false;
          this.isLoadingKitStatus = false;
        }
      });
    } else {
      this.isLoadingKitStatus = false;
    }
  }

  private disableMandatoryConsents(): void {
    if (this.consentForm && this.consents) {
      this.consents.forEach(c => {
        if (c.mandatory) {
          const controlName = 'consent_' + c.id;
          const control = this.consentForm.get(controlName);
          if (control) {
            control.disable();
            control.setValue(true);
          }
        }
      });
    }
  }

  private captureInitialOptionalConsentValues(): void {
    if (!this.consentForm || !this.consents) {
      return;
    }

    this.initialOptionalConsentValues = {};

    this.consents
      .filter(c => !c.mandatory)
      .forEach(c => {
        const controlName = 'consent_' + c.id;
        const control = this.consentForm.get(controlName);
        this.initialOptionalConsentValues[c.id] = !!control?.value;
      });

    this.hasOptionalConsentChanged = false;
  }

  onOptionalConsentChange(): void {
    if (!this.consentForm || !this.consents) {
      return;
    }

    this.hasOptionalConsentChanged = this.consents
      .filter(c => !c.mandatory)
      .some(c => {
        const controlName = 'consent_' + c.id;
        const control = this.consentForm.get(controlName);
        const currentValue = !!control?.value;
        const initialValue = this.initialOptionalConsentValues[c.id] ?? false;
        return currentValue !== initialValue;
      });
  }


  private buildPayload() {
    const formValues = this.consentForm.value;

    const consentIdList = Object.keys(formValues)
      .filter(key => formValues[key] === true)
      .map(key => Number(key.replace('consent_', '')));

    return {
      patientId: null,
      consentIdList
    };
  }

  onFocusEvent(event: any) {
    this.myFocusVar = true;
  }


  onSave() {
    if (!this.consentForm) {
      return;
    }

    if (!this.consentForm.valid) {
      this.consentForm.markAllAsTouched();
      this.snackBar.error('Please confirm all mandatory consents before saving.');
      return;
    }

    const payload = this.buildEditPayload();
    payload.patientId = this.patientId ?? null;

    const consentDialogRef = this.dialog.open(EditMyConsentComponent, {
      width: '630px',
      disableClose: true,
      autoFocus: false,
      data: { payload },
    });

    this.captureInitialOptionalConsentValues();
  }

  private buildEditPayload() {
    const formValues = this.consentForm.value;

    const mandatoryIds = [1, 2, 3, 4, 5, 8];

    const selectedIds = Object.keys(formValues)
      .filter(key => formValues[key] === true)
      .map(key => Number(key.replace('consent_', '')));

    const optionalSelectedIds = selectedIds.filter(id => !mandatoryIds.includes(id));

    const consentIdList = [...mandatoryIds, ...optionalSelectedIds];

    return {
      patientId: null,
      consentIdList
    };
  }

  orderTest(): void {
    if (!this.patientId) {
      this.snackBar.error('Patient information not available. Please try again.');
      return;
    }

    this.pservice.checkPatientsConsent(this.patientId).subscribe({
      next: (data) => {
        if (data === true) {
          this.openGenomeDataOrder();
        } else {
          const dialogRef = this.dialog.open(PatientConsentComponent, {
            width: "650px",
            height: "600px",
            disableClose: true,
            autoFocus: false,
            data: { patientId: this.patientId },
          });
          dialogRef.afterClosed().subscribe((res) => {
            if (res === true) {
              this.openGenomeDataOrder();
            }
          });
        }
      },
      error: (error) => {
        this.snackBar.error(error.error?.applicationMessage || 'Failed to check patient consent');
      }
    });
  }

  private openGenomeDataOrder(): void {
    const dialogRef = this.dialog.open(GenomeDataOrderComponent, {
      width: "630px",
      height: "400px",
      disableClose: true,
      autoFocus: false,
      data: { patientId: this.patientId },
    });

    dialogRef.afterClosed().subscribe((result) => {

      if (result === true) {
        this.hasKitRegistered = true;
        this.disableMandatoryConsents();
      } else if (result === 'cancel') {
        return;
      } else {
        setTimeout(() => {
          this.checkIfTestOrdered();
        }, 500);
      }
    });
  }

}

